import axios from 'axios';
import { getTenantKey } from '@/lib/tenant';
import { trackPhotoRelay, recordSubmittedVisitType } from '@/lib/embedBridge';
import { normalizeModalityConfig, getChosenVisitModality } from '@/lib/visitModality';

// §video-visit: the patient's online/video choice rides on every Beluga visit
// create — only when the checkout offered it (null → field absent, BE default).
const withVisitModality = (body) => {
  const m = getChosenVisitModality();
  if (m) body.visitModality = m;
  return body;
};
const _visitModalityCache = new Map();

// Create axios instance with base configuration
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// §consult-fee dedupe — see getCheckoutConfig. Keyed by visitType; holds the
// in-flight/resolved promise so concurrent readers share one request.
const _checkoutConfigCache = new Map();

// §0.B — Embed-mode detection. The patient-dashboard wrapper opens the
// intake/follow-up routes inside an iframe with `?embed=1`. The query
// param persists via react-router for the lifetime of the form session,
// so we can read it from window.location at submit time without threading
// it through every component. Backend uses this flag to decide whether
// to skip credential issuance + welcome email (the dashboard already owns
// the auth surface).
const isEmbedMode = () => {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).get('embed') === '1';
  } catch {
    return false;
  }
};

// §authenticated-patient handoff: relay the signed ?pt= token on submits so the
// BE attaches the visit to the existing patient account (no "email already
// exists", correct tenant). Read from sessionStorage (persisted by
// lib/handoff.js — read inline here, not imported, to avoid an api↔handoff import
// cycle). handoff.js CLEARS this key when the token resolves invalid/expired, so
// a dead token is never relayed; undefined when there's no handoff → unchanged.
const handoffToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem('fitpal_handoff_token') || null;
  } catch {
    return null;
  }
};

const api = axios.create({
  baseURL: BACKEND_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 10 seconds timeout
});

// §tenant-branded intake §3/§4: thread the session tenant key onto the calls
// that need it — `t` param for catalog/branding reads (tenant pricing overlay),
// `tenantKey` in the body for row-creating submits (BE resolves → stamps
// tenant_id + Auth0 org membership). No key → untouched (default Fitpal / tenant 1).
const TENANT_PARAM_URLS = ['/catalog/medications', '/tenant/config'];
const TENANT_BODY_URLS = [
  '/beluga/create-visit-v2',
  '/payments/square-payment-create-payment',
  '/payments/validate-coupon',
  '/beluga/lab-authorization',
  '/beluga/resolve-intake-handoff',
  '/followup/submit',
  '/followup/lookup-user',
  '/beluga/upload-visit-photo-submission',
  '/leads',
  '/otp/send',
  '/otp/verify',
  '/advanced-peptide/submit',
  '/advanced-peptide/waitlist',
  '/concierge/oto/enroll',
];
api.interceptors.request.use((config) => {
  const key = getTenantKey();
  if (!key) return config;
  const url = config.url || '';
  if (TENANT_PARAM_URLS.some((u) => url.startsWith(u))) {
    config.params = { ...(config.params || {}), t: key };
  } else if (TENANT_BODY_URLS.some((u) => url.startsWith(u))) {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (!config.data.has('tenantKey')) config.data.append('tenantKey', key);
    } else if (config.data && typeof config.data === 'object') {
      config.data = { ...config.data, tenantKey: key };
    }
  }
  return config;
});

// Request interceptor for logging/auth
api.interceptors.request.use(
  (config) => {
    console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`);
    return config;
  },
  (error) => {
    console.error('[API Request Error]', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log(`[API Response] ${response.status} ${response.config.url}`);
    return response;
  },
  (error) => {
    const message = error.response?.data?.error || error.message || 'An error occurred';
    console.error('[API Response Error]', message);
    return Promise.reject(error);
  }
);

// API methods
export const apiService = {
  // Submit form. consentsSigned is hardcoded true at this layer because the
  // FE flow cannot reach this call without the patient clicking through every
  // consent screen — Beluga requires formObj.consentsSigned === true.
  // `extras` is an optional bag for visit-type-specific envelope fields
  // (e.g. TRT: belugaOrdersLabs, labMethod, scores, planId, selectedPlans).
  // It merges shallowly into the body so legacy callers stay unchanged.
  submitForm: async (answers, contact, paymentId, flow, visitType, extras = {}) => {
    // §embed-bridge: remember the exact visitType Beluga is given.
    recordSubmittedVisitType(visitType);
    // §pharmacy-smoke hijack: when ?smoke=pharmacy is in the URL, swap the
    // submission to Beluga's generic test visit type (testVisitType) per
    // BE's 2026-05-26 contract. Backend keys off flow='test_pharmacy_smoke'
    // to bypass clinical validation and force pharmacyId=271328 +
    // medId=testVisitType server-side. We strip the questionnaire (answers=[])
    // because testVisitType has no Q/A. PaymentId is whatever the existing
    // Square flow tokenized — backend uses it for verifyPayment as normal.
    //
    // Usage:
    //   1. Walk any intake (TRT is shortest) with ?smoke=pharmacy in the URL
    //   2. Complete Square sandbox payment normally
    //   3. On submit, this hijack swaps flow/visitType/answers
    //   4. Backend returns masterId — share with Beluga
    //
    // See docs/BACKEND_PHARMACY_WEBHOOKS_PRESMOKE_PRD.md
    let body = {
      answers, contact, paymentId, flow, visitType, consentsSigned: true,
      dashboardEmbed: isEmbedMode(),
      ...extras,
    };

    // §authenticated-patient handoff: relay the signed handoff token so the BE
    // re-verifies it and attaches the visit to the existing account. Present only
    // when this session launched with ?pt= (and it resolved valid); absent → a
    // normal new-patient submit, unchanged.
    const handoff = handoffToken();
    if (handoff) body.intakeHandoffToken = handoff;
    withVisitModality(body);

    // §auth0-account: the Square payment response carries the patient's one-time
    // Auth0 password-setup link (BE created the account on charge). Relay it so
    // the welcome email renders a "Set your password" button instead of a
    // plaintext temp password. Null for returning users / Auth0 errors.
    // See docs/FE_PRD_INTAKE_AUTH0_ACCOUNT.md (BE: project_auth0_dual_accept).
    try {
      const setupUrl =
        typeof window !== 'undefined' ? localStorage.getItem('fitpal_auth0_setup_url') : null;
      if (setupUrl) body.auth0PasswordSetupUrl = setupUrl;
    } catch (e) { /* no storage — skip */ }

    // §staging-signoff: opt a submission into Beluga STAGING relay for the
    // trt/trtFollowup sign-off campaign. Read-only from the URL (?belugaEnv=
    // staging) so it never reaches real patients — the prod build of the form
    // is never served with that query param. The prod BE additionally gates on
    // visitType ∈ {trt,trtFollowup} + the BELUGA_STAGING_TEST_VISITS_ENABLED
    // kill-switch, so an absent field is 100% unchanged prod behavior.
    // See docs/FE_TRT_STAGING_SIGNOFF_SMOKE_PRD.md §1.1.
    try {
      const belugaEnv =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('belugaEnv')
          : null;
      if (belugaEnv === 'staging') {
        body.belugaEnv = 'staging';
        console.warn('[staging-signoff] belugaEnv=staging → BE relays to Beluga staging');
      }
    } catch (e) { /* SSR or no window — ignore */ }
    try {
      const smokeParams =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search)
          : new URLSearchParams('');
      const smokeMode = smokeParams.get('smoke');
      const labMethodParam = smokeParams.get('labMethod'); // 'inPerson' | 'atHome' | null
      if (smokeMode === 'pharmacy') {
        console.warn(
          '[pharmacy-smoke] hijacking submission → flow=test_pharmacy_smoke, visitType=testVisitType' +
            (labMethodParam ? `, belugaOrdersLabs=true, labMethod=${labMethodParam}` : ''),
        );
        // Hijack rules (per Beluga ops 2026-05-27 follow-up):
        // - Walk any intake (TRT shortest), tokenize Square, then swap the
        //   submission to Beluga's testVisitType contract.
        // - Keep ONLY the sex-at-birth answer — Beluga's field validator
        //   requires formObj.sex (the 2026-05-26 "Sex error" reject).
        // - **Explicitly** send patientPreference with medId=testVisitType
        //   and pharmacyId=271328. Previous smokes relied on the backend to
        //   force these, but Beluga reported the resulting visits didn't
        //   carry the testVisitType medId — likely because the TRT extras
        //   (planId, selectedPlans, belugaOrdersLabs, labMethod, scores)
        //   were passed through and overrode the BE's forcing logic.
        // - Strip every TRT-specific extra so nothing the BE might branch
        //   on can flip us back into the TRT codepath.
        const {
          // discard these from the spread; intentionally unused vars
          /* eslint-disable no-unused-vars */
          planId: _planId,
          selectedPlans: _selectedPlans,
          belugaOrdersLabs: _belugaOrdersLabs,
          labMethod: _labMethod,
          scores: _scores,
          /* eslint-enable no-unused-vars */
          ...rest
        } = body;
        body = {
          ...rest,
          answers: [
            { slide_id: 'sex-at-birth', question: 'sex', answer: 'Male', rawAnswer: 'male' },
          ],
          flow: 'test_pharmacy_smoke',
          visitType: 'testVisitType',
          smokeMode: true,
          pharmacyId: '271328',
          // Match the shape Beluga's field validator accepts (strings, not
          // numbers — same shape the BE mapper emits for real plans).
          // The 2026-05-27 smoke hit "patientPreference fields error" when
          // quantity/refills were numeric. daysSupply added per
          // reference_beluga_api.md's documented field set.
          patientPreference: [
            {
              name: 'Test Medication',
              strength: '1mg',
              quantity: '1',
              refills: '0',
              daysSupply: '30',
              medId: 'testVisitType',
            },
          ],
        };

        // Lab smoke variant (Beluga 2026-05-28 ask): when ?labMethod=inPerson
        // or ?labMethod=atHome is set, add the lab integration fields so
        // Beluga routes the visit to their lab partner. NY/NJ are inPerson-
        // only per BACKEND_TRT_LAB_INTEGRATION_PRD §2; the smoke uses WV so
        // both methods are valid.
        if (labMethodParam === 'inPerson' || labMethodParam === 'atHome') {
          body.belugaOrdersLabs = true;
          body.labMethod = labMethodParam;
        }
      }
    } catch (e) { /* SSR or no window — ignore */ }

    // Detect smoke mode for the partial-success recovery branch below.
    let isPharmacySmoke = false;
    try {
      isPharmacySmoke =
        typeof window !== 'undefined' &&
        new URLSearchParams(window.location.search).get('smoke') === 'pharmacy';
    } catch (e) { /* ignore */ }

    try {
      const response = await api.post('/beluga/create-visit-v2', body);
      return response.data;
    } catch (err) {
      // Partial-success recovery: backend may return 5xx when Beluga accepted
      // the visit (masterId minted) but the post-Beluga local recording step
      // failed. The masterId is still load-bearing — we should surface it to
      // the UI instead of swallowing it into a generic submit-failure alert.
      // Gated to smoke mode so real customers still see the failure alert
      // (a partial-success in prod means our DB is out of sync with Beluga,
      // which IS an actionable error for the real flow).
      const data = err?.response?.data;
      if (isPharmacySmoke && data?.masterId) {
        console.warn(
          '[pharmacy-smoke] backend 5xx but masterId returned — recovering:',
          data.masterId,
        );
        return data; // looks like a success body to the caller
      }
      throw err;
    }
  },

  // update form
  updateForm: async (answers,primaryGoal,currentStep,timestamp,paymentId) => {
    const response = await api.post('/payments/update-front-intake-form-details', {answers,primaryGoal,currentStep,timestamp,paymentId });
    return response.data;
  },

  // email is required for the followup flow so the backend can match the
  // returning user; new-patient flows omit it (backend resolves via
  // answers[basic-contact].email instead). Backend handler at
  // /payments/square-payment-create-payment reads body.email at top level.
  // §coupons (Square-native, BACKEND_SQUARE_PAYMENTS_METHODS_AND_COUPONS_PRD):
  // validate a coupon code server-side against Square's discounts for the given
  // cart, returning the discount + discounted total (cents). The BE is the source
  // of truth — the FE only DISPLAYS this; the real discount is re-applied on
  // capture from the couponCode sent to create-payment. Soft-degrades to invalid.
  validateCoupon: async ({ code, planId, selectedMedIds, primaryGoal }) => {
    try {
      const res = await api.post('/payments/validate-coupon', {
        code: (code || '').trim(),
        planId: (Array.isArray(selectedMedIds) && selectedMedIds.length) ? undefined : planId,
        selectedMedIds: (Array.isArray(selectedMedIds) && selectedMedIds.length) ? selectedMedIds : undefined,
        primaryGoal,
      });
      return res.data; // { valid, label, discountCents, discountedTotalCents, currency }
    } catch (err) {
      const data = err?.response?.data;
      return { valid: false, error: data?.message || 'That code isn’t valid.' };
    }
  },
  // `extra` (object, optional) — §branded-refill quote path: { quoteId, visitType,
  // pharmacySelection }. The BE charges the stored quote's totalCents and
  // verifies the leg; 409 quote_stale (errorCode QUOTE_STALE) = nothing charged.
  createSquarePayment: async (token, planId, answers, primaryGoal, currentStep, timestamp, email, selectedMedIds, prescriptionFulfillment, verificationToken, name, phoneVerifyToken, couponCode, extra = {}) => {
    const body = { token, answers, primaryGoal, currentStep, timestamp, dashboardEmbed: isEmbedMode() };
    // §membership (docs/BACKEND_MEMBERSHIP_SUBSCRIPTION_PRD.md §11): the BE
    // stores the card on file (CreateCard) to charge both the one-time med
    // payment AND the $59/mo membership subscription from one tokenization.
    // verificationToken = Square SCA buyer-verification token (needed for
    // CreateCard); name = patient name for CreateCustomer. Harmless extras
    // until the BE membership path ships — it ignores unknown fields.
    if (verificationToken) body.verificationToken = verificationToken;
    if (name) body.name = name;
    // §catalog: when the cart is catalog-driven, send selectedMedIds — the BE
    // prices from drug_catalog (computePricing + tenant markup), so the charge
    // == the displayed cart total, and planId is ignored (BE eeabef5). Omit
    // planId in that case. Otherwise keep the legacy planId pricing path.
    if (Array.isArray(selectedMedIds) && selectedMedIds.length) {
      body.selectedMedIds = selectedMedIds;
    } else {
      body.planId = planId;
    }
    // §regular_rx local pharmacy: for a regular_rx med filled at the patient's
    // own pharmacy, the BE charges the CONSULT FEE (consultFeeCents) instead of
    // med retail. The BE computes the charged amount server-side from this flag
    // (never trust an FE amount). 'ship' / undefined → med retail (unchanged).
    if (prescriptionFulfillment) body.prescriptionFulfillment = prescriptionFulfillment;
    if (email) body.email = email;
    // §otp (docs/BACKEND_PHONE_OTP_AND_LEAD_CAPTURE_PRD.md §1): short-lived
    // token proving the patient's phone was SMS-verified this checkout, so the
    // BE can trust the verified phone server-side (not just an FE boolean).
    // Only present when REACT_APP_OTP_ENABLED is on; ignored by the BE until
    // the OTP path ships. dark otherwise.
    if (phoneVerifyToken) body.phoneVerifyToken = phoneVerifyToken;
    // §coupons: the BE re-validates + applies the Square discount server-side and
    // charges the discounted amount (never trust an FE amount). Harmless until the
    // coupon path ships — the BE ignores unknown fields.
    if (couponCode) body.couponCode = couponCode;
    // §branded-refill: bind the payment to the follow-up quote. visitType is the
    // quote's own string echoed verbatim (casing is not uniform across visit
    // types and the bare 'followup' is not an alias).
    if (extra && extra.quoteId) body.quoteId = extra.quoteId;
    if (extra && extra.visitType) body.visitType = extra.visitType;
    if (extra && extra.pharmacySelection) body.pharmacySelection = extra.pharmacySelection;
    // §concierge-oto (BE #348): the backend keeps the card for the one-click
    // offer ONLY when this flag is sent — verificationToken alone no longer
    // does, because membership/add-on checkouts send that too. Sent under the
    // exact condition that renders the card-save notice (OtoCardSaveNotice).
    if (extra && extra.otoCardOnFile === true) body.otoCardOnFile = true;
    // BE #349: the server CHARGES the DEXA add-on from this (1 → 9900, 3 → 19900,
    // added after coupons). Absent when nothing was added. 400 dexa_invalid /
    // 409 dexa_price_changed {cents} → nothing charged.
    if (extra && extra.dexaAddOn) body.dexaAddOn = extra.dexaAddOn;
    const response = await api.post('/payments/square-payment-create-payment', body);
    return response.data;
  },
  getActivePlanList: async (token, planId) => {
    const response = await api.get('/client-plan/active-list-v2');
    return response.data;
  },
  // §pricing-ownership (BE is source of truth): fetch active client_plan rows and
  // return a { plan_slug: plan_price(dollars) } map so checkouts render/charge
  // the BE price instead of a hardcoded literal. See
  // reference_pricing_medid_ownership. Public endpoint.
  getActivePlanPrices: async () => {
    const res = await api.get('/client-plan/active-list');
    const arr = Array.isArray(res.data) ? res.data : (res.data?.List || res.data?.list || res.data?.data || []);
    const map = {};
    (arr || []).forEach((p) => { if (p && p.plan_slug != null) map[p.plan_slug] = p.plan_price; });
    return map;
  },
  getUserExists: async (email) => {
    const response = await api.post('/user-exist-v2', email);
    return response.data;
  },
  // §authenticated-patient handoff: resolve the signed ?pt= token to the real
  // patient (identity source of truth). Public, gated by the token.
  // → { valid:true, tenantId, patient:{ id,email,firstName,lastName,phone,dob } }
  //   | { valid:false } for an invalid/expired/forged token.
  resolveIntakeHandoff: async (token) => {
    const response = await api.post('/beluga/resolve-intake-handoff', { token });
    return response.data;
  },
  getFormDetailsWithPaymentId: async (payment_id) => {
    const response = await api.get(`/payments/get-front-intake-form-details?payment_id=${payment_id}`);
    return response.data;
  },
  // Backend resolver priority: masterId > email > phone (last 10 digits).
  // Backwards-compatible: if a string is passed, treat as legacy email.
  // Embed callers pass an object with any of {masterId, email, phone}.
  lookupFollowUpUser: async (idOrEmail, legacyPhone) => {
    let body;
    if (typeof idOrEmail === 'object' && idOrEmail !== null) {
      body = {};
      if (idOrEmail.masterId) body.masterId = idOrEmail.masterId;
      if (idOrEmail.email) body.email = idOrEmail.email;
      if (idOrEmail.phone) body.phone = idOrEmail.phone;
    } else {
      body = { email: idOrEmail };
      if (legacyPhone) body.phone = legacyPhone;
    }
    const response = await api.post('/followup/lookup-user', body);
    return response.data;
  },
  // §branded-refill (BE PR #336, dark behind FOLLOWUP_QUOTES_ENABLED):
  // GET /followup/quote?masterId=&rxId= (or &medId= when the Rx row has no rxId)
  // [&fulfillmentRoute=local_pharmacy|beluga_partner][&requestedMedId=<strengths[].medId>]
  // No identity, no session, no ?t= — keyed on the prior visit + the Rx, the
  // same posture as lookup-user. 404 while the flag is off (caller → today's
  // flow). Errors: 404 rx_not_found; 422 route_not_allowed | invalid_dose |
  // fee_not_configured | quote_unavailable. Throws on non-2xx.
  // masterId alone quotes the visit's LATEST Rx and resolves a null medId from
  // the catalog (BE #337/#339, live 2026-10-07); rxId / rxRowId / medId narrow.
  getFollowupQuote: async ({ masterId, rxId, rxRowId, medId, fulfillmentRoute, requestedMedId } = {}) => {
    const params = { masterId };
    if (rxId) params.rxId = rxId;
    else if (rxRowId) params.rxRowId = rxRowId;
    else if (medId) params.medId = medId;
    if (fulfillmentRoute) params.fulfillmentRoute = fulfillmentRoute;
    if (requestedMedId) params.requestedMedId = requestedMedId;
    const response = await api.get('/followup/quote', { params });
    return response.data;
  },
  // §concierge-oto (docs/BACKEND_CONCIERGE_OTO_PRD.md §2): one-click enrolment
  // into a concierge program right after a treatment visit, charged to the
  // card the backend stored at that checkout. Idempotent per (masterId,
  // program). Errors: 402 card_declined | no_card_on_file, 404 visit_not_found,
  // 409 already_enrolled, 422 state_not_served | price_mismatch.
  enrollConciergeOto: async (body) => {
    const response = await api.post('/concierge/oto/enroll', body);
    return response.data; // { ok, enrollmentId, intakeId, subscriptionId, chargedCents, monthlyCents, nextChargeAt }
  },
  // BE #347: the offer as the server prices and words it — per program
  // { todayCents, monthlyCents, consentText, consentSha256 }. The FE prices
  // the buttons from THIS (never from its constants) and shows consentText
  // verbatim; the server records that text as the consent given on click.
  // 404 while CONCIERGE_OTO_ENABLED is off → the page is skipped.
  getConciergeOtoOffer: async ({ masterId, paymentId } = {}) => {
    const response = await api.get('/concierge/oto/offer', { params: { masterId, paymentId } });
    return response.data; // { programs: { advancedPeptide: {...}, ... } } or an array of { program, ... }
  },
  // data: { answers, contact, paymentId, flow, visitType, email, ... }
  // consentsSigned is added here for the same reason as submitForm above.
  submitFollowUp: async (data) => {
    const body = { ...data, consentsSigned: true, dashboardEmbed: isEmbedMode() };
    // §authenticated-patient handoff: attach to the existing account (see submitForm).
    const handoff = handoffToken();
    if (handoff) body.intakeHandoffToken = handoff;
    withVisitModality(body);
    // §staging-signoff: opt a trtFollowup sign-off submission into the Beluga
    // STAGING relay (?belugaEnv=staging). Read-only from the URL — prod builds
    // never carry it, and the BE gates on visitType∈{trt,trtFollowup} + the
    // STAGING_TEST_BYPASS sentinel + kill-switch, so an absent field is 100%
    // unchanged prod behavior. Mirrors submitForm. See
    // docs/FE_TRT_STAGING_SIGNOFF_SMOKE_PRD.md §1.1.
    try {
      const belugaEnv =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('belugaEnv')
          : null;
      if (belugaEnv === 'staging') {
        body.belugaEnv = 'staging';
        console.warn('[staging-signoff] belugaEnv=staging → BE relays followup to Beluga staging');
      }
    } catch (e) { /* SSR or no window — ignore */ }
    const response = await api.post('/followup/submit', body);
    return response.data;
  },

  // §patient-address (FE_PRD_INTAKE_PATIENT_ADDRESS_UPDATE §1.1). JWT-only
  // routes; `token` is the dashboard session token from lib/sessionBridge. It is
  // set per request, never on the shared instance, so it can't ride along on
  // any other call.
  // → { current: {address,city,state,zip} | null, history: [...] }
  getPatientAddress: async (masterId, token) => {
    const response = await api.get('/beluga/patient-address', {
      params: { masterId },
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },
  // Beluga first, then the users-row mirror + history. → { ok, address, previous, updatedAt }
  updatePatientAddress: async ({ masterId, address, city, state, zip }, token) => {
    const response = await api.post(
      '/beluga/update-patient-address',
      { masterId, address, city, state, zip },
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  // Post-checkout photo upload. Used for slides that collect photos AFTER
  // /beluga/create-visit-v2 returns (currently identity-verification +
  // full-body-image on the GLP-1 flow). slideId tells the backend which Beluga
  // file slot to relay to via setVisitFile.
  // photo: { fileName, fileType, preview: 'data:<mime>;base64,<...>' } as FE
  // collects from FileUploadStep. masterId + visitId come from the
  // /beluga/create-visit-v2 response.
  uploadVisitPhoto: async ({ masterId, visitId, slideId, photo }) => {
    if (!photo || !photo.preview) throw new Error('uploadVisitPhoto: missing photo.preview');
    // Decode base64 data URL → Blob so we can send multipart/form-data.
    const [meta, b64] = photo.preview.split(',');
    const mimeMatch = /data:([^;]+);base64/.exec(meta);
    const mime = (mimeMatch && mimeMatch[1]) || photo.fileType || 'image/jpeg';
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });

    const form = new FormData();
    form.append('masterId', String(masterId));
    if (visitId !== undefined && visitId !== null) form.append('visitId', String(visitId));
    form.append('slideId', slideId);
    form.append('files', blob, photo.fileName || `${slideId}.jpg`);

    // §embed-bridge: register the in-flight relay so the visit-complete
    // postMessage waits for it. The dashboard CLOSES its modal on that message,
    // which unmounts this iframe and aborts anything still uploading — emitting
    // immediately would trade a working ID upload for a faster toast. Tracking
    // here (the one function every photo-relaying form calls) keeps the nine
    // relay loops untouched.
    const req = api.post('/beluga/upload-visit-photo-submission', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    trackPhotoRelay(req);
    const response = await req;
    return response.data;
  },

  // §magic-link (FE_PRD magic sign-in): email the patient a one-click sign-in
  // link to their dashboard (no password). Enumeration-safe — BE returns
  // 200 {ok:true, expires_in:900} for any well-formed email (silently no-ops if
  // no account). 400 invalid_email; 429 too_many (rate limit 3/5min). The
  // emailed link lands on dashboard.fitpal.com/magic-link/verify — the intake
  // app only triggers the send.
  startMagicLink: async (email) => {
    const res = await api.post('/api/auth/magic-link/start', { email });
    return res.data; // { ok, expires_in }
  },

  // §catalog (FE_PRD catalog-driven meds): fetch the BE's ACTIVE, priced,
  // medId-bearing SKUs for a visitType (optionally a pharmacy source:
  // beluga|hallandale|southend). Public endpoint (no auth). Returns items with
  // { id, name, medication, dose, strength, sig, medId, pharmacy, pharmacyId,
  //   quantity, refills, daySupply, price(cents), imageUrl }.
  // §tenant-branded intake §2: public branding + pricing config for a tenant key
  // (Host-resolved when no key). The interceptor appends ?t=<key> automatically.
  getTenantConfig: async (key) => {
    const response = await api.get('/tenant/config', { params: key ? { t: key } : {} });
    return response.data;
  },
  // §square-config — BE-sourced public Square client config + consult fee
  // (docs/BACKEND_SQUARE_SUBSCRIPTIONS_BILLING_PRD, Patrick's FE PRD 2026-07-31):
  // GET /consult-fee returns { feeCents, currency, square: { applicationId,
  // locationId, environment } }. The `square` block is the SAME merchant the BE
  // charges, so sourcing it here keeps FE tokenize + BE capture on one merchant
  // (kills the app-id/merchant-mismatch class of bug). Public (no auth); ?t=
  // auto-threaded. Returns null on any error so the caller falls back to env.
  // Deduped per visitType for the life of the page. TWO components on the same
  // checkout screen need this response — SquarePaymentForm for the Square
  // merchant config, CatalogCheckoutStep for the consult fee — and they were
  // firing identical requests. Sharing the in-flight promise makes the second
  // reader free, which is what makes reading the fee from here (rather than from
  // the catalog row's copy) cost nothing.
  //
  // Only successful responses are retained: a null result is evicted so a later
  // mount can retry rather than inheriting one transient failure for the session.
  getCheckoutConfig: (visitType) => {
    const key = visitType || '';
    if (_checkoutConfigCache.has(key)) return _checkoutConfigCache.get(key);
    const p = api
      .get('/consult-fee', { params: visitType ? { visitType } : {} })
      .then((res) => res.data)
      .catch(() => null)
      .then((data) => {
        if (data == null) _checkoutConfigCache.delete(key);
        return data;
      });
    _checkoutConfigCache.set(key, p);
    return p;
  },
  // §video-visit (BE #383): GET /intake/visit-modality?visitType=&state=[&flow=]
  // → { modality, priceDeltaCents, reason? } (lib/visitModality.js). `flow`
  // disambiguates branded flows that share a visitType. Never rejects: any
  // failure (bare 404 while the BE flag is off) resolves to 'unavailable'.
  // Deduped per key; failures are evicted so a later mount can retry.
  getVisitModality: ({ visitType, state, flow } = {}) => {
    const key = `${visitType || ''}|${state || ''}|${flow || ''}`;
    if (_visitModalityCache.has(key)) return _visitModalityCache.get(key);
    let failed = false;
    const params = { visitType, state };
    if (flow) params.flow = flow;
    const p = api
      .get('/intake/visit-modality', { params })
      .then((res) => res.data)
      .catch(() => { failed = true; return null; })
      .then((data) => {
        if (failed) _visitModalityCache.delete(key);
        return normalizeModalityConfig(data);
      });
    _visitModalityCache.set(key, p);
    return p;
  },
  // §otp — phone verification at checkout (docs/BACKEND_PHONE_OTP_AND_LEAD_CAPTURE_PRD.md).
  // tenantKey auto-threaded by the interceptor. Send a 6-digit SMS code:
  sendOtp: async (phone) => {
    const res = await api.post('/otp/send', { phone });
    return res.data;
  },
  // Verify the code → { verified, phoneVerifyToken?, locked? }
  verifyOtp: async (phone, code) => {
    const res = await api.post('/otp/verify', { phone, code });
    return res.data;
  },
  // §lead-capture — persist the started-but-not-finished patient so the dashboard
  // can re-target. Fire-and-forget: never throws / never blocks the checkout.
  captureLead: async (lead) => {
    try {
      const res = await api.post('/leads', lead);
      return res.data;
    } catch (e) {
      console.warn('[lead-capture] failed (non-blocking)', e && e.message);
      return null;
    }
  },
  getMedications: async ({ visitType, pharmacy } = {}) => {
    const params = {};
    if (visitType) params.visitType = visitType;
    if (pharmacy) params.pharmacy = pharmacy;
    const response = await api.get('/catalog/medications', { params });
    return response.data?.items || [];
  },

  // §lab-auto-auth: submit a 60-biomarker panel order to Beluga's
  // labTestingAutoAuthorization visit (immediate provider approval) via the BE
  // proxy POST /beluga/lab-authorization → Beluga POST /lab/labAuthorization.
  // formObj carries demographics + testTypes[] (+ optional Q/A); no
  // patientPreference. Backend contract: docs/BACKEND_LAB_AUTOAUTH_VISIT_PRD.md.
  submitLabAuthorization: async ({ formObj, paymentId }) => {
    // masterId is minted by the BE proxy before relaying to Beluga (same as
    // create-visit-v2 — the FE never generates masterIds).
    const body = {
      formObj: { ...formObj, consentsSigned: true },
      company: 'fitpal',
      visitType: 'labTestingAutoAuthorization',
      paymentId,
      dashboardEmbed: isEmbedMode(),
    };
    withVisitModality(body);
    // §authenticated-patient handoff: attach to the existing account (see submitForm).
    const handoff = handoffToken();
    if (handoff) body.intakeHandoffToken = handoff;
    // §auth0-account: relay the password-setup link for the welcome email.
    try {
      const setupUrl =
        typeof window !== 'undefined' ? localStorage.getItem('fitpal_auth0_setup_url') : null;
      if (setupUrl) body.auth0PasswordSetupUrl = setupUrl;
    } catch (e) { /* no storage — skip */ }
    // §staging-signoff: opt into Beluga STAGING relay (?belugaEnv=staging).
    // Read-only from the URL; prod builds never carry the param.
    try {
      const belugaEnv =
        typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('belugaEnv')
          : null;
      if (belugaEnv === 'staging') {
        body.belugaEnv = 'staging';
        console.warn('[staging-signoff] belugaEnv=staging → BE relays lab auth to Beluga staging');
      }
    } catch (e) {
      /* SSR or no window — ignore */
    }
    const response = await api.post('/beluga/lab-authorization', body);
    return response.data;
  },

  // §advanced-peptide (docs/BACKEND_ADVANCED_PEPTIDE_PRD.md): NOT a Beluga
  // visit. The practice's own async-telehealth intake. Body is assembled by
  // AdvancedPeptideForm.submitOrder; this adds the shared envelope fields.
  // → { intakeId, threadId, status }
  submitAdvancedPeptideIntake: async (payload) => {
    const body = { ...payload, consentsSigned: true, dashboardEmbed: isEmbedMode() };
    const handoff = handoffToken();
    if (handoff) body.intakeHandoffToken = handoff;
    // §auth0-account: relay the password-setup link for the welcome email.
    try {
      const setupUrl = typeof window !== 'undefined' ? localStorage.getItem('fitpal_auth0_setup_url') : null;
      if (setupUrl) body.auth0PasswordSetupUrl = setupUrl;
    } catch (e) { /* no storage — skip */ }
    // §staging-bypass: the BE gate needs belugaEnv:"staging" next to the
    // STAGING_TEST_BYPASS paymentId (BE PR #318 §7). Read-only from the URL.
    try {
      const belugaEnv =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('belugaEnv') : null;
      if (belugaEnv === 'staging') body.belugaEnv = 'staging';
    } catch (e) { /* SSR or no window — ignore */ }
    const response = await api.post('/advanced-peptide/submit', body);
    return response.data;
  },
  // Government-ID photo for an Advanced Peptide intake → the practice's own
  // file store (multipart, same Blob decode as uploadVisitPhoto; no Beluga).
  // paymentId is the intake's own (`psub_…` or the bypass sentinel) — the BE
  // 404s the upload without it.
  uploadAdvancedPeptideFile: async ({ intakeId, slideId, photo, paymentId }) => {
    if (!photo || !photo.preview) throw new Error('uploadAdvancedPeptideFile: missing photo.preview');
    const [meta, b64] = photo.preview.split(',');
    const mimeMatch = /data:([^;]+);base64/.exec(meta);
    const mime = (mimeMatch && mimeMatch[1]) || photo.fileType || 'image/jpeg';
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    const form = new FormData();
    form.append('slideId', slideId);
    if (paymentId) form.append('paymentId', paymentId);
    form.append('files', new Blob([bytes], { type: mime }), photo.fileName || `${slideId}.jpg`);
    const req = api.post(`/advanced-peptide/${encodeURIComponent(intakeId)}/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    trackPhotoRelay(req);
    const response = await req;
    return response.data;
  },
  // Non-served state → launch-updates waitlist. Public; { email, state }.
  joinAdvancedPeptideWaitlist: async ({ email, state }) => {
    const response = await api.post('/advanced-peptide/waitlist', { email, state });
    return response.data;
  },

  // §pharmacy-choice (branded weightloss): proxy to Beluga POST
  // /external/pharmacies via the backend (Beluga API keys live server-side).
  // At least one of {name, city, state, zip} is required (Beluga returns 400
  // "Invalid search parameters" otherwise); response is up to 100 matches of
  // { PharmacyId, StoreName, Address1, City, State, ZipCode, PrimaryPhone, … }.
  // Backend contract: docs/BACKEND_BRANDED_WEIGHTLOSS_VISIT_PRD.md §4a.
  searchPharmacies: async ({ name, city, state, zip } = {}) => {
    const body = {};
    if (name) body.name = name;
    if (city) body.city = city;
    if (state) body.state = state;
    if (zip) body.zip = zip;
    const response = await api.post('/beluga/pharmacy-search', body);
    return response.data;
  },
};

export default api;